import React, { useRef, useState } from 'react';
import { Box, IconButton, CircularProgress, Typography } from '@mui/material';
import { Upload, Trash2 } from 'lucide-react';
import {
  getConventionalFilename,
  getTimestampField,
  logger,
} from '@musedrops/shared';
import { supabase } from '../../../services/supabase';

interface AudioFileFieldProps {
  /** Display label for the field */
  label: string;
  /** Current filename (not full path) */
  value?: string | null;
  /** Callback when file is uploaded - receives filename only */
  // eslint-disable-next-line no-unused-vars
  onUpload?: (filename: string) => void;
  /** Callback when file is deleted */
  onDelete?: () => void;
  /** Callback when timestamp changes (for immediate cache-busting) */
  // eslint-disable-next-line no-unused-vars
  onTimestampChanged?: (timestamp: string) => void;
  /** Callback for AI generation */
  onRegenerate?: () => void;
  /** Audio URL for playback (should include timestamp for cache-busting) */
  audioUrl?: string;
  /** Storage folder type */
  storageFolder: 'shows' | 'stories';
  /** Entity slug for constructing storage path */
  slug: string;
  /** Entity ID for database updates */
  entityId?: string;
  /** Database table name - includes story_translations and show_translations for multi-language support */
  tableName: 'shows' | 'stories' | 'story_translations' | 'show_translations';
  /** File type for conventional naming */
  fileType:
    | 'introMusic'
    | 'outroMusic'
    | 'backgroundMusic'
    | 'themeMusic'
    | 'showAnnounce'
    | 'storyAnnounce'
    | 'voice'
    | 'voiceAudio'
    | 'mixedAudio'
    | 'externalAudio';
  /** Language code for language-specific files (e.g., 'en', 'es') */
  lang?: string;
  /** Accepted file types */
  accept?: string;
  /** Disable upload button */
  disabled?: boolean;
  /** Show upload button */
  showUpload?: boolean;
  /** Show generate/regenerate button */
  showGenerate?: boolean;
  /** Custom upload title */
  uploadTitle?: string;
  /** Generate button tooltip */
  regenerateTitle?: string;
  /** Is generation in progress */
  isRegenerating?: boolean;
  /** Custom icon for generate button */
  RegenerateIcon?: React.ComponentType<{ size?: number }>;
  /** Helper text to display */
  helperText?: string;
}

/**
 * Shared Audio File Field Component
 *
 * Provides upload, playback, and delete functionality for audio files with:
 * - Icon buttons for upload/generate
 * - Inline HTML5 audio player
 * - Delete icon button
 * - Timestamp-based cache-busting
 * - Error handling and loading states
 *
 * Consolidated from IntroMusicSection, OutroMusicSection, MusicUploadSection patterns
 */
export const AudioFileField: React.FC<AudioFileFieldProps> = ({
  label,
  value,
  onUpload,
  onDelete,
  onTimestampChanged,
  onRegenerate,
  audioUrl,
  storageFolder,
  slug,
  entityId,
  tableName,
  fileType,
  lang,
  accept = 'audio/*',
  disabled = false,
  showUpload = true,
  showGenerate = false,
  uploadTitle = 'Upload audio file',
  regenerateTitle = 'Generate audio using AI',
  isRegenerating = false,
  RegenerateIcon,
  helperText,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUploadClick = () => {
    if (!slug) {
      setError('Cannot upload: entity must be saved first to generate slug');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('audio/')) {
      setError('Please select an audio file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Use conventional filename
      const filename = getConventionalFilename(
        storageFolder === 'shows' ? 'show' : 'story',
        fileType,
        lang
      );
      const storagePath = `${storageFolder}/${slug}/${filename}`;

      // Upload to Supabase Storage (upsert=true to overwrite existing)
      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Update only timestamp (files use conventional names)
      // Handle composite keys for translation tables
      if (entityId) {
        const timestampField = getTimestampField(
          storageFolder === 'shows' ? 'show' : 'story',
          fileType
        );

        const updateData = {
          [timestampField]: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        let query = supabase.from(tableName).update(updateData);

        // Translation tables use composite keys
        if (tableName === 'show_translations' && lang) {
          // show_translations: composite key (show_slug, lang)
          // entityId here is the show slug
          query = query.eq('show_slug', entityId).eq('lang', lang);
        } else if (tableName === 'story_translations' && lang) {
          // story_translations: composite key (story_slug, lang)
          // entityId here is the story slug
          query = query.eq('story_slug', entityId).eq('lang', lang);
        } else {
          // Regular tables: single id column
          query = query.eq('id', entityId);
        }

        const { error: updateError } = await query;

        if (updateError) {
          logger.error('Failed to update timestamp:', updateError);
          throw new Error(`Failed to update timestamp: ${updateError.message}`);
        }
      }

      // Notify parent components
      onUpload?.(filename);

      onTimestampChanged?.(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!value || !slug || !entityId || !onDelete) return;

    setDeleting(true);
    setError(null);

    try {
      // Delete file from storage
      const filePath = `${storageFolder}/${slug}/${value}`;
      const { error: deleteError } = await supabase.storage
        .from('media')
        .remove([filePath]);

      if (deleteError) {
        logger.error('Failed to delete file from storage:', deleteError);
      }

      // Clear timestamp field (files use conventional names)
      // Handle composite keys for translation tables
      const timestampField = getTimestampField(
        storageFolder === 'shows' ? 'show' : 'story',
        fileType
      );

      const updateData = {
        [timestampField]: null,
        updated_at: new Date().toISOString(),
      };

      let query = supabase.from(tableName).update(updateData);

      // Translation tables use composite keys
      if (tableName === 'show_translations' && lang) {
        query = query.eq('show_slug', entityId).eq('lang', lang);
      } else if (tableName === 'story_translations' && lang) {
        query = query.eq('story_slug', entityId).eq('lang', lang);
      } else {
        query = query.eq('id', entityId);
      }

      const { error: updateError } = await query;

      if (updateError) {
        logger.error('Failed to clear timestamp:', updateError);
        setError('Failed to delete file');
        return;
      }

      onDelete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const canUpload = showUpload && slug && !uploading && !disabled && onUpload;
  const canGenerate = showGenerate && onRegenerate;
  const canDelete = value && slug && entityId && onDelete && !deleting;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Label */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>

      {/* Icon Buttons + Audio Player Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
        }}
      >
        {/* Left: Upload Icon */}
        {canUpload && (
          <IconButton
            onClick={handleUploadClick}
            disabled={!canUpload || uploading}
            title={uploadTitle}
            size="small"
          >
            {uploading ? <CircularProgress size={20} /> : <Upload size={20} />}
          </IconButton>
        )}

        {/* Left: Generate Icon */}
        {canGenerate && RegenerateIcon && (
          <IconButton
            onClick={onRegenerate}
            disabled={isRegenerating}
            title={regenerateTitle}
            size="small"
          >
            {isRegenerating ? (
              <CircularProgress size={20} />
            ) : (
              <RegenerateIcon size={20} />
            )}
          </IconButton>
        )}

        {/* Middle: Audio Player (when file exists and not processing) */}
        {audioUrl && value && !uploading && !isRegenerating ? (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio
              controls
              src={audioUrl}
              style={{
                width: '100%',
                height: '32px',
                maxHeight: '32px',
              }}
            />
          </Box>
        ) : uploading || isRegenerating ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flex: 1, fontStyle: 'italic' }}
          >
            {uploading ? 'Uploading...' : 'Generating...'}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ flex: 1, fontStyle: 'italic' }}
          >
            {value || 'No file'}
          </Typography>
        )}

        {/* Right: Delete Icon (hidden while processing) */}
        {canDelete && !uploading && !isRegenerating && (
          <IconButton
            onClick={handleDelete}
            disabled={deleting}
            title="Delete audio file"
            size="small"
            color="error"
          >
            {deleting ? <CircularProgress size={20} /> : <Trash2 size={20} />}
          </IconButton>
        )}
      </Box>

      {/* Helper Text */}
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}

      {/* Error Message */}
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {error}
        </Typography>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </Box>
  );
};
